import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, googleProvider, db } from "../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import bgL from "../assets/loginBg.png";
import bgD from "../assets/loginBg2.png";
import { FcGoogle } from "react-icons/fc";

export default function Login({ theme }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const navigate = useNavigate();

  // --- Normal Email Login ---
  async function handleSubmit(e) {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/app");
    } catch (err) {
      setError(err.message);
    }
  }

  // --- Google Login Logic ---
  async function handleGoogleLogin() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user exists in Firestore
      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      // If user logs in via Google but has no Firestore doc, create a default one (FREE tier)
      if (!docSnap.exists()) {
        await setDoc(docRef, {
          name: user.displayName,
          email: user.email,
          createdAt: new Date(),
          subscriptionTier: "FREE",
        });
      }

      navigate("/");
    } catch (err) {
      setError(err.message);
    }
  }

  // --- Forgot Password Handler ---
  const handleForgotPassword = async () => {
    setResetError("");
    setResetMessage("");

    if (!resetEmail) {
      setResetError("Please enter your email address");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage(
        `Password reset email sent to ${resetEmail}. Please check your inbox.`
      );
      setResetEmail("");

      // Auto-close modal after 3 seconds
      setTimeout(() => {
        setShowResetModal(false);
        setResetMessage("");
      }, 3000);
    } catch (err) {
      console.error("Password reset error:", err);
      if (err.code === "auth/user-not-found") {
        setResetError("No account found with this email address.");
      } else if (err.code === "auth/invalid-email") {
        setResetError("Invalid email address.");
      } else {
        setResetError("Failed to send reset email. Please try again.");
      }
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Login - ASites Planner";
  }, []);

  return (
    <Wrapper theme={theme}>
      {/* Password Reset Modal */}
      {showResetModal && (
        <ModalOverlay onClick={() => setShowResetModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Reset Password</h3>
              <CloseButton onClick={() => setShowResetModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <p>
                Enter your email address and we'll send you a link to reset your
                password.
              </p>

              <Input
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                style={{ marginBottom: "15px" }}
              />

              {resetMessage && <SuccessMessage>{resetMessage}</SuccessMessage>}

              {resetError && <ErrorMessage>{resetError}</ErrorMessage>}

              <ModalActions>
                <SecondaryButton onClick={() => setShowResetModal(false)}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleForgotPassword}>
                  Send Reset Link
                </PrimaryButton>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      <Container>
        <Box>
          <h2>Login</h2>
          {error && <p style={{ color: "red" }}>{error}</p>}
          <form onSubmit={handleSubmit}>
            <Input
              type="email"
              placeholder="Email"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
            />
            <Input
              type="password"
              placeholder="Password"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
            />

            <ForgotPassword onClick={() => setShowResetModal(true)}>
              Forgot Password?
            </ForgotPassword>

            <Button type="submit">Login</Button>
          </form>

          <Divider>or continue with</Divider>

          <SocialButtons>
            <SocialBtn type="button" onClick={handleGoogleLogin}>
              <FcGoogle size={22} />
              Google
            </SocialBtn>
          </SocialButtons>

          <p style={{ marginTop: "2rem" }}>
            Don't have an account?{" "}
            <Link to="/signup" style={{ color: "white" }}>
              Sign up
            </Link>
          </p>
        </Box>
      </Container>

      <ImageSection theme={theme} />
    </Wrapper>
  );
}

/* ---------- STYLED COMPONENTS ---------- */

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  overflow: hidden;
  transition: 0.5s;

  @media (max-width: 1450px) {
    background: url(${bgD});
    background-size: cover;
    background-position: center;
    flex-direction: column;
  }
`;

const Container = styled.div`
  width: 40%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1b2130;
  color: white;
  transition: 0.5s;
  h2 {
    margin-bottom: 2em;
  }

  @media (max-width: 1450px) {
    width: 100%;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(8px);
    min-height: 100vh;
    padding: 20px;
  }
  @media (max-width: 500px) {
    width: 100%;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(8px);
    min-height: 100vh;
    padding: 0px;
  }
`;

const Box = styled.div`
  position: relative;
  overflow: hidden;
  background: #313a5069;
  padding: 50px 40px;
  border-radius: 16px;
  width: 70%;
  text-align: center;
  box-shadow: 0 2px 10px #313a5069;
  backdrop-filter: blur(15px);
  transition: 0.5s;

  &::before {
    content: "";
    position: absolute;
    top: -60px;
    right: -100px;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle,
      rgba(45, 136, 211, 0.35),
      rgba(255, 255, 255, 0) 70%
    );
    pointer-events: none;
    filter: blur(10px);
  }

  @media (max-width: 500px) {
    width: 100%;
    padding: 40px 20px;
  }
  @media (max-width: 1450px) {
    width: 90%;
  }
`;

const SocialButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const SocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background-color: #2c3348;
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: none;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: 0.5s;
  font-family: "Montserrat";
  color: white;
  &:hover {
    filter: brightness(0.9);
  }
`;

const Divider = styled.p`
  text-align: center;
  color: #b6b6b6;
  margin-top: 20px;
  font-size: 0.9rem;
`;

const ForgotPassword = styled.p`
  text-align: right;
  color: #b6b6b6;
  cursor: pointer;
  font-size: 0.85rem;
  margin-bottom: 1rem;
  transition: color 0.2s;

  &:hover {
    color: #fff;
    text-decoration: underline;
  }
`;

const ImageSection = styled.div`
  width: 60%;
  background: url(${bgD});
  background-size: cover;
  background-position: center;
  transition: 0.5s;
  z-index: 99999999999;
  position: relative;

  @media (max-width: 1450px) {
    display: none;
  }
`;

const Input = styled.input`
  padding: 25px 20px;
  width: 100%;
  border-radius: 20px;
  border: none;
  font-family: "Montserrat", sans-serif;
  font-size: 0.9rem;
  background: #1b2130;
  outline: none;
  color: white;
  box-shadow: 0 0 10px 1px #313a5069;
  margin-bottom: 10px;
  transition: 0.5s;

  &::placeholder {
    color: #888;
  }
`;

const Button = styled.button`
  background: #495269;
  color: white;
  border: none;
  width: 100%;
  font-family: "Montserrat", sans-serif;
  padding: 20px;
  border-radius: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: #333;
  }
`;

/* ---------- MODAL STYLES ---------- */

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #1b2130;
  border-radius: 16px;
  padding: 30px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid #313a50;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    margin: 0;
    color: white;
    font-size: 1.3rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b6b6b6;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  line-height: 1;
  transition: color 0.2s;

  &:hover {
    color: white;
  }
`;

const ModalBody = styled.div`
  p {
    color: #b6b6b6;
    margin-bottom: 20px;
    line-height: 1.5;
  }
`;

const SuccessMessage = styled.p`
  color: #4ade80 !important;
  background: rgba(74, 222, 128, 0.1);
  padding: 10px;
  border-radius: 8px;
  margin-top: 10px !important;
  margin-bottom: 15px !important;
`;

const ErrorMessage = styled.p`
  color: #f87171 !important;
  background: rgba(248, 113, 113, 0.1);
  padding: 10px;
  border-radius: 8px;
  margin-top: 10px !important;
  margin-bottom: 15px !important;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const PrimaryButton = styled.button`
  flex: 1;
  background: #495269;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 10px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  background: #2c3348;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 10px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: #3a4158;
  }
`;
